import type { Config } from 'dompurify';
import createDOMPurify from 'dompurify';

const DOMPurify = createDOMPurify();

/**
 * Constants for secure rel attribute values.
 */
export const REL_NOOPENER = 'noopener';
export const REL_NOREFERRER = 'noreferrer';

DOMPurify?.addHook?.('afterSanitizeAttributes', (node: Element) => {
    const rel = node.getAttribute?.('rel') ?? '';
    const hasNoReferrerAndNoOpener = [REL_NOOPENER, REL_NOREFERRER].every((attr) =>
        rel.split(' ').includes(attr),
    );

    if ('target' in node && hasNoReferrerAndNoOpener) {
        node!.setAttribute?.('target', '_blank');
    }
});

/**
 * Sanitizes potentially dangerous HTML markup using DOMPurify.
 *
 * This function uses DOMPurify to clean HTML content, removing potentially
 * malicious scripts and other unsafe elements.
 *
 * @param {string} potentiallyDangerousMarkup - The HTML string to sanitize.
 * @param {Config} [config] - Optional DOMPurify configuration options.
 * @returns {string} Sanitized HTML string.
 *
 * @example
 * const customConfig = { ALLOWED_TAGS: ['p', 'a'] };
 * const cleanHtml = sanitize('<script>alert("XSS")</script><p>Safe content</p>', customConfig);
 * // Result: '<p>Safe content</p>'
 *
 */
export function sanitize(potentiallyDangerousMarkup: string, config?: Config): string {
    if (!DOMPurify?.addHook) {
        return potentiallyDangerousMarkup;
    }
    if (config) {
        return DOMPurify.sanitize(potentiallyDangerousMarkup, {
            ...config,
            RETURN_DOM_FRAGMENT: false,
            RETURN_DOM: false,
        });
    }

    return DOMPurify.sanitize(potentiallyDangerousMarkup);
}
